// src/helpers/availability.js
import { getData } from './ApiSettings';

/**
 * Add buffer time to dates for availability check
 * @param {Date} date - The date to add buffer to
 * @param {boolean} isStart - Whether this is a start date (subtract hours) or end date (add hours)
 * @returns {Date} - Date with buffer applied
 */
const applyDateBuffer = (date, isStart) => {
  const bufferedDate = new Date(date);
  if (isStart) {
    bufferedDate.setHours(bufferedDate.getHours() - 24);
  } else {
    bufferedDate.setHours(bufferedDate.getHours() + 24);
  }
  return bufferedDate;
};

/**
 * Check if two date ranges overlap
 * @param {Date} start1 - Start of first range
 * @param {Date} end1 - End of first range
 * @param {Date} start2 - Start of second range
 * @param {Date} end2 - End of second range
 * @returns {boolean} - True if ranges overlap
 */
const doDateRangesOverlap = (start1, end1, start2, end2) => {
  const s1 = new Date(start1).getTime();
  const e1 = new Date(end1).getTime();
  const s2 = new Date(start2).getTime();
  const e2 = new Date(end2).getTime();
  return s1 <= e2 && e1 >= s2;
};

/**
 * Get available quantity for a product during a specific date range
 * @param {string} productDocId - Product document ID
 * @param {Date} startDate - Start date
 * @param {Date} endDate - End date
 * @param {number} requestedQuantity - Optional quantity being requested
 * @returns {Promise<{available: boolean, availableQuantity: number}>}
 */
export const checkProductAvailability = async (productDocId, startDate, endDate, requestedQuantity = 1) => {
  try {
    // Get product details to know total quantity
    const productResponse = await getData(`/products?filters[id][$eq]=${productDocId}&populate=*`);
    const product = productResponse.data.data[0];
    if (!product) throw new Error('Product not found');
    
    const totalQuantity = product.quantity || 1;
    
    // Add buffer to check dates
    const bufferStartDate = applyDateBuffer(startDate, true);
    const bufferEndDate = applyDateBuffer(endDate, false);

    // Get all order items for this product
    const response = await getData(`/order-items?populate=*`);
    const orderItems = response.data.data || [];

    // Find overlapping bookings
    const overlappingBookings = orderItems.filter(item => {
      // Check if this is for our product
      if (item.product?.id !== productDocId) return false;

      // Check date overlap
      const itemStartDate = new Date(item.start_date);
      const itemEndDate = new Date(item.end_date);

      return doDateRangesOverlap(
        bufferStartDate,
        bufferEndDate,
        itemStartDate,
        itemEndDate
      );
    });

    // Calculate total booked quantity for this period
    const totalBookedQuantity = overlappingBookings.reduce((sum, booking) => {
      return sum + (booking.quantity || 1);
    }, 0);

    const availableQuantity = totalQuantity - totalBookedQuantity;

    // console.log('Availability check:', {
    //   productDocId,
    //   totalQuantity,
    //   totalBookedQuantity,
    //   availableQuantity,
    //   requestedQuantity,
    //   dates: {
    //     start: bufferStartDate,
    //     end: bufferEndDate
    //   }
    // });

    return {
      available: availableQuantity >= requestedQuantity,
      availableQuantity
    };
  } catch (error) {
    console.error('Error checking availability:', error);
    return {
      available: false,
      availableQuantity: 0
    };
  }
};

/**
 * Get all dates where product is fully booked or has insufficient quantity
 * @param {string} productDocId - Product document ID
 * @param {number} requestedQuantity - Optional quantity being requested
 * @returns {Promise<Array<{start: Date, end: Date}>>}
 */
export const getUnavailableDateRanges = async (productDocId, requestedQuantity = 1) => {
  try {
    // Get product details
    const productResponse = await getData(`/products?filters[id][$eq]=${productDocId}&populate=*`);
    const product = productResponse.data.data[0];
    if (!product) throw new Error('Product not found');
    
    const totalQuantity = product.quantity || 1;

    // Get all bookings
    const response = await getData(`/order-items?populate=*`);
    const orderItems = response.data.data || [];

    // Group bookings by date ranges and sum quantities
    const bookingsByDate = new Map();

    orderItems
      .filter(item => item.product?.id === productDocId)
      .forEach(item => {
        const start = new Date(item.start_date);
        const end = new Date(item.end_date);
        const quantity = item.quantity || 1;
        
        // Create a key for each day in the range
        let currentDate = new Date(start);
        while (currentDate <= end) {
          const dateKey = currentDate.toISOString().split('T')[0];
          const currentQuantity = bookingsByDate.get(dateKey) || 0;
          bookingsByDate.set(dateKey, currentQuantity + quantity);
          currentDate.setDate(currentDate.getDate() + 1);
        }
      });

    // Find date ranges where remaining quantity is less than requested
    const insufficientQuantityDates = [];
    for (const [dateStr, bookedQuantity] of bookingsByDate) {
      if (totalQuantity - bookedQuantity < requestedQuantity) {
        const date = new Date(dateStr);
        insufficientQuantityDates.push({
          start: applyDateBuffer(date, true),
          end: applyDateBuffer(date, false)
        });
      }
    }

    // console.log('Dates with insufficient quantity:', insufficientQuantityDates);
    return insufficientQuantityDates;
  } catch (error) {
    console.error('Error getting unavailable dates:', error);
    return [];
  }
};